import { getAddress, isAddress } from "ethers";
/**
 * Validates that a required string parameter is not empty or just whitespace.
 *
 * @param value - The value to validate
 * @param fieldName - The name of the field for error messages
 * @throws {Error} When the value is empty or just whitespace
 */
function validateRequiredString(value, fieldName) {
    if (!value || String(value).trim() === "") {
        throw new Error(`${fieldName} is required`);
    }
}
/**
 * Validates that a required address parameter is not empty and is a valid address format.
 * Supports Ethereum (0x...), Solana (Base58), and Stacks (SP/ST...) addresses.
 *
 * @param value - The address to validate
 * @param fieldName - The name of the field for error messages
 * @throws {Error} When the address is empty or invalid
 */
function validateRequiredAddress(value, fieldName) {
    validateRequiredString(value, fieldName);
    const address = String(value);
    if (!isValidMultiChainAddress(address)) {
        throw new Error(`Invalid ${fieldName.toLowerCase()} format: '${value}' must be a valid address. Supported formats: Ethereum (0x...), Solana (Base58), or Stacks (SP/ST...)`);
    }
}
/**
 * Validates if an address is in a supported format (Ethereum, Solana, or Stacks).
 *
 * @param address - The address to validate
 * @returns True if the address format is valid for any supported chain
 */
function isValidMultiChainAddress(address) {
    return (isValidEthereumAddress(address) ||
        isValidSolanaAddress(address) ||
        isValidStacksAddress(address));
}
/**
 * Validates Ethereum address format (0x followed by 40 hex characters).
 * First checksums the address to EIP-55 format, then validates it.
 *
 * @param address - The address to validate
 * @returns True if the address is a valid Ethereum address
 */
function isValidEthereumAddress(address) {
    try {
        // First checksum the address to EIP-55 format
        const checksummedAddress = getAddress(address);
        // Then validate the checksummed address
        return isAddress(checksummedAddress);
    }
    catch {
        // If checksumming fails, the address is invalid
        return false;
    }
}
/**
 * Validates Solana address format (Base58, typically 32-44 characters).
 *
 * @param address - The address to validate
 * @returns True if the address is a valid Solana address
 */
function isValidSolanaAddress(address) {
    // Solana addresses are Base58 encoded and typically 32-44 characters
    // They don't start with 0x and contain only Base58 characters
    if (address.startsWith("0x") || address.length < 32 || address.length > 44) {
        return false;
    }
    // Base58 alphabet: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
}
/**
 * Validates Stacks address format (Base58, starts with SP or ST).
 *
 * @param address - The address to validate
 * @returns True if the address is a valid Stacks address
 */
function isValidStacksAddress(address) {
    // Stacks addresses start with SP (mainnet) or ST (testnet) and are Base58 encoded
    if (!address.startsWith("SP") && !address.startsWith("ST")) {
        return false;
    }
    // Should be around 40-41 characters total
    if (address.length < 40 || address.length > 41) {
        return false;
    }
    // Base58 alphabet: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
}
/**
 * Validates that an array has at least the minimum required length.
 *
 * @param value - The array to validate
 * @param minLength - The minimum required length
 * @param fieldName - The name of the field for error messages
 * @throws {Error} When the array is too short
 */
function validateArrayMinLength(value, minLength, fieldName) {
    if (!Array.isArray(value) || value.length < minLength) {
        throw new Error(`${fieldName} must contain at least ${minLength} ${minLength === 2 ? "token addresses" : "items"}`);
    }
}
/**
 * Validates swap transaction parameters for DEX operations.
 *
 * @param params - The swap transaction parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateSwapTransactionParams(params) {
    validateRequiredString(params.dexId, "DEX ID");
    validateArrayMinLength(params.path, 2, "Path");
    validateRequiredString(params.amountIn, "Amount in");
    validateRequiredAddress(params.to, "Recipient address");
    validateRequiredAddress(params.from, "Sender address");
    validateRequiredString(params.deadline, "Transaction deadline");
}
/**
 * Validates bridge token identifier: must be either (srcTokenSymbol + dstTokenSymbol) or (srcTokenAddress + dstTokenAddress), not both.
 *
 * @param params - The token identifier parameters
 * @throws {Error} When neither pair is complete, or when both are provided
 */
function validateBridgeTokenIdentifier(params) {
    const hasSymbols = typeof params.srcTokenSymbol === "string" &&
        params.srcTokenSymbol.trim() !== "" &&
        typeof params.dstTokenSymbol === "string" &&
        params.dstTokenSymbol.trim() !== "";
    const hasAddresses = typeof params.srcTokenAddress === "string" &&
        params.srcTokenAddress.trim() !== "" &&
        typeof params.dstTokenAddress === "string" &&
        params.dstTokenAddress.trim() !== "";
    if (!hasSymbols && !hasAddresses) {
        throw new Error("Bridge requires either (srcTokenSymbol and dstTokenSymbol) or (srcTokenAddress and dstTokenAddress)");
    }
    if (hasSymbols && hasAddresses) {
        throw new Error("Bridge must use either token symbols or token addresses, not both");
    }
    if (hasAddresses) {
        validateRequiredAddress(params.srcTokenAddress, "Source token address");
        validateRequiredAddress(params.dstTokenAddress, "Destination token address");
    }
}
/**
 * Validates bridge transaction parameters for cross-chain operations.
 * Tokens must be specified by either symbol (srcTokenSymbol + dstTokenSymbol) or by address (srcTokenAddress + dstTokenAddress), not both.
 *
 * @param params - The bridge transaction parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateBridgeTransactionParams(params) {
    validateRequiredString(params.bridgeId, "Bridge ID");
    validateRequiredString(params.dstChainId, "Destination chain ID");
    validateRequiredString(params.amountIn, "Amount in");
    validateRequiredAddress(params.from, "Sender address");
    validateRequiredAddress(params.to, "Recipient address");
    validateBridgeTokenIdentifier({
        srcTokenSymbol: params.srcTokenSymbol,
        dstTokenSymbol: params.dstTokenSymbol,
        srcTokenAddress: params.srcTokenAddress,
        dstTokenAddress: params.dstTokenAddress,
    });
}
/**
 * Validates bridge status request parameters.
 *
 * @param params - The bridge status request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateBridgeStatusParams(params) {
    validateRequiredString(params.transactionHash, "Transaction hash");
    validateRequiredString(params.bridgeId, "Bridge ID");
}
/**
 * Validates swap quote request parameters.
 *
 * @param params - The swap quote request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateSwapQuoteParams(params) {
    validateRequiredString(params.chainId, "Chain ID");
    validateRequiredString(params.path, "Path");
    validateRequiredString(params.amountIn, "Amount in");
}
/**
 * Validates bridge quote request parameters.
 * Tokens must be specified by either symbol (srcTokenSymbol + dstTokenSymbol) or by address (srcTokenAddress + dstTokenAddress), not both.
 *
 * @param params - The bridge quote request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateBridgeQuoteParams(params) {
    validateRequiredString(params.dstChainId, "Destination chain ID");
    validateRequiredString(params.amountIn, "Amount in");
    validateBridgeTokenIdentifier({
        srcTokenSymbol: params.srcTokenSymbol,
        dstTokenSymbol: params.dstTokenSymbol,
        srcTokenAddress: params.srcTokenAddress,
        dstTokenAddress: params.dstTokenAddress,
    });
}
/**
 * Validates lendborrow deposit request parameters.
 *
 * @param params - The lendborrow deposit request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateLendborrowDepositParams(params) {
    validateRequiredAddress(params.asset, "Asset address");
    validateRequiredString(params.amount, "Amount");
    validateRequiredAddress(params.onBehalfOf, "OnBehalfOf address");
    validateRequiredAddress(params.from, "From address");
}
/**
 * Validates lendborrow withdraw request parameters.
 *
 * @param params - The lendborrow withdraw request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateLendborrowWithdrawParams(params) {
    validateRequiredString(params.lendborrowId, "Lendborrow ID");
    validateRequiredAddress(params.asset, "Asset address");
    validateRequiredString(params.amount, "Amount");
    validateRequiredAddress(params.to, "Recipient address");
}
/**
 * Validates lendborrow pool request parameters.
 *
 * @param params - The lendborrow pool request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateLendborrowPoolParams(params) {
    validateRequiredAddress(params.asset, "Asset address");
    // Optional parameters validation
    if (params.lendborrowId !== undefined) {
        validateRequiredString(params.lendborrowId, "Lendborrow ID");
    }
    if (params.marketId !== undefined) {
        validateRequiredString(params.marketId, "Market ID");
    }
    if (params.vaultAddress !== undefined) {
        validateRequiredAddress(params.vaultAddress, "Vault address");
    }
}
/**
 * Validates lendborrow pools request parameters.
 *
 * @param params - The lendborrow pools request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateLendborrowPoolsParams(params) {
    if (params.assets !== undefined) {
        validateRequiredString(params.assets, "Assets");
    }
    // Optional parameters validation
    if (params.lendborrowId !== undefined) {
        validateRequiredString(params.lendborrowId, "Lendborrow ID");
    }
    if (params.address !== undefined) {
        validateRequiredAddress(params.address, "Address");
    }
    if (params.markets !== undefined) {
        validateRequiredString(params.markets, "Markets");
    }
    if (params.vaults !== undefined) {
        validateRequiredString(params.vaults, "Vaults");
    }
}
/**
 * Validates user positions request parameters.
 *
 * @param params - The user positions request parameters
 * @throws {Error} When any required parameter is missing or invalid
 */
export function validateUserPositionsParams(params) {
    validateRequiredAddress(params.address, "Address");
    // Optional parameters validation
    if (params.lendborrowId !== undefined) {
        validateRequiredString(params.lendborrowId, "Lendborrow ID");
    }
    if (params.vsCurrencies !== undefined) {
        validateRequiredString(params.vsCurrencies, "VS Currencies");
    }
    if (params.marketId !== undefined) {
        validateRequiredString(params.marketId, "Market ID");
    }
    if (params.vaultAddress !== undefined) {
        validateRequiredAddress(params.vaultAddress, "Vault address");
    }
}
/**
 * Type guard to check if an object has the expected dual-transaction structure.
 * Returns true only when the response contains `createFeeTxn` or `allowTxn`,
 * indicating a dual transaction response (when enableFee: true).
 *
 * @param responseData - The response data object to validate
 * @returns true if it is a non-null object with at least one of the expected properties
 */
export function hasDualDepositTxProperties(responseData) {
    if (responseData === null || typeof responseData !== "object") {
        return false;
    }
    return "createFeeTxn" in responseData || "allowTxn" in responseData;
}
/**
 * Asserts swap quote required fields for quote items.
 */
export function assertSwapQuoteRequiredFields(params) {
    if (!params.chainId)
        throw new Error("Missing field: chainId");
    if (!Array.isArray(params.path) || params.path.length === 0) {
        throw new Error("Missing field: path");
    }
    if (typeof params.amountIn !== "string" || params.amountIn.trim() === "") {
        throw new Error("Missing field: amountIn");
    }
    if (!Array.isArray(params.amountsOut) || params.amountsOut.length === 0) {
        throw new Error("Missing field: amountsOut");
    }
    // Normalize presence, but keep original fields without forcing defaults
    const rawId = (typeof params.dexId === "string" && params.dexId.trim() !== ""
        ? params.dexId
        : undefined) ||
        (typeof params.bridgeId === "string" && params.bridgeId.trim() !== ""
            ? params.bridgeId
            : undefined);
    const rawName = (typeof params.dexName === "string" && params.dexName.trim() !== ""
        ? params.dexName
        : undefined) ||
        (typeof params.bridgeName === "string" && params.bridgeName.trim() !== ""
            ? params.bridgeName
            : undefined);
    const providerId = rawId || "unknown_provider";
    const providerName = rawName || "Unknown Provider";
    return {
        chainId: params.chainId,
        path: params.path,
        amountIn: params.amountIn,
        amountsOut: params.amountsOut,
        providerId,
        providerName,
    };
}
/**
 * Asserts bridge quote required fields for quote items.
 */
export function assertBridgeQuoteRequiredFields(params) {
    // srcChainId may be empty when not provided by the caller; allow empty string
    if (!params.dstChainId)
        throw new Error("Missing field: dstChainId");
    if (typeof params.srcTokenSymbol !== "string" || params.srcTokenSymbol.trim() === "") {
        throw new Error("Missing field: srcTokenSymbol");
    }
    if (typeof params.dstTokenSymbol !== "string" || params.dstTokenSymbol.trim() === "") {
        throw new Error("Missing field: dstTokenSymbol");
    }
    if (typeof params.amountIn !== "string" || params.amountIn.trim() === "") {
        throw new Error("Missing field: amountIn");
    }
    if (!Array.isArray(params.amountsOut)) {
        throw new Error("Missing field: amountsOut");
    }
    const rawId = (typeof params.bridgeId === "string" && params.bridgeId.trim() !== ""
        ? params.bridgeId
        : undefined) ||
        (typeof params.dexId === "string" && params.dexId.trim() !== ""
            ? params.dexId
            : undefined);
    const rawName = (typeof params.bridgeName === "string" && params.bridgeName.trim() !== ""
        ? params.bridgeName
        : undefined) ||
        (typeof params.dexName === "string" && params.dexName.trim() !== ""
            ? params.dexName
            : undefined);
    return {
        srcChainId: params.srcChainId,
        dstChainId: params.dstChainId,
        srcTokenSymbol: params.srcTokenSymbol,
        dstTokenSymbol: params.dstTokenSymbol,
        amountIn: params.amountIn,
        amountsOut: params.amountsOut,
        providerId: rawId || "unknown_provider",
        providerName: rawName || "Unknown Provider",
    };
}
/**
 * Validates parameters for getApiMetadataV2 request.
 *
 * @param params - The parameters to validate
 * @throws {Error} When category or endpoint is missing or empty
 */
export function validateApiMetadataV2Params(params) {
    validateRequiredString(params.category, "Category");
    validateRequiredString(params.endpoint, "Endpoint");
}
//# sourceMappingURL=data:application/json;base64,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